import { Alert, Button, Modal, Skeleton, Space, Typography } from 'antd';
import React, { useRef } from 'react';

import { TagManager } from '@/components/common/TagManager';
import {
  CrepeMarkdownField,
  type CrepeMarkdownFieldHandle,
} from '@/components/editor/CrepeMarkdownField';
import { useAssetContext, type KnowledgeCardNotePayloadDTO } from '@/hooks/useAssetContext';
import { LinkIcon as LinkOutlined } from '@/icons';

const { Text } = Typography;

/** Props for the `AssetContextModal` that lets users view, edit, and link a knowledge card. */
export interface AssetContextModalProps {
  open: boolean;
  cardId: string | null;
  accessToken: string | null;
  clientId: string;
  projectId: string;
  onLink: (cardId: string) => void;
  onClose: () => void;
  /** Seed card data directly — skips the API fetch (used by Storybook / tests). */
  initialCard?: KnowledgeCardNotePayloadDTO;
}

/** Modal that loads a knowledge card by ID, lets the user edit its content and tags, and links it to the current assessment on save. */
export const AssetContextModal: React.FC<AssetContextModalProps> = ({
  open,
  cardId,
  accessToken,
  clientId,
  projectId,
  onLink,
  onClose,
  initialCard,
}) => {
  const editorRef = useRef<CrepeMarkdownFieldHandle | null>(null);
  const {
    card,
    isLoading,
    isSaving,
    loadError,
    saveError,
    editTitle,
    editContent,
    tags,
    setEditTitle,
    setEditContent,
    setTags,
    handleSaveAndLink,
  } = useAssetContext(open, cardId, accessToken, clientId, projectId, onLink, initialCard);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="80vw"
      title={
        <input
          aria-label="Card title"
          className="create-note-title-input"
          placeholder="Enter title..."
          disabled={isLoading || !card}
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
        />
      }
      styles={{
        content: {
          height: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          background: 'var(--bg-subtle)',
        },
        header: { padding: '12px 24px', flexShrink: 0, background: 'var(--bg-subtle)' },
        body: { flex: 1, overflowY: 'auto', padding: '16px 24px', background: 'var(--bg-subtle)' },
        footer: { padding: '12px 24px', flexShrink: 0, background: 'var(--bg-subtle)' },
      }}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={isSaving}
            disabled={isLoading}
            onClick={() =>
              void handleSaveAndLink(() => editorRef.current?.getMarkdown() ?? editContent)
            }
          >
            Save & Link
          </Button>
        </Space>
      }
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {loadError && <Alert type="warning" showIcon message={loadError} />}
          {saveError && <Alert type="error" showIcon message={saveError} />}

          {card && (
            <>
              <TagManager
                tags={tags}
                onTagsChange={setTags}
                accessToken={accessToken ?? ''}
                clientId={clientId}
                cardId={cardId}
                localOnly
              />

              <div
                style={{
                  display: 'flex',
                  justifyContent: card.source_url ? 'space-between' : 'flex-end',
                  alignItems: 'center',
                }}
              >
                {card.source_url && (
                  <Typography.Link href={card.source_url} target="_blank" rel="noopener noreferrer">
                    View original document <LinkOutlined />
                  </Typography.Link>
                )}
                <Text type="secondary">
                  Milkdown tip: use slash commands in the editor with <code>/</code>.
                </Text>
              </div>

              <CrepeMarkdownField
                ref={editorRef}
                bootKey={`asset-context-modal-${cardId}`}
                defaultMarkdown={editContent}
                onMarkdownChange={setEditContent}
                minHeight={320}
              />
            </>
          )}

          {!card && !loadError && !isLoading && (
            <Text type="secondary">No context available yet. You can still link this asset.</Text>
          )}
        </Space>
      )}
    </Modal>
  );
};

export default AssetContextModal;
